import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { parseDecimal, intOpts, toFieldText } from "@/lib/decimal";
import { NumberField } from "@/components/DecimalInput";
import { Calculator, Loader2, AlertTriangle } from "lucide-react";

const QUELLE = { standard: "Standardwerte der Website", druckparameter: "aus den Druckparametern des Auftrags", vorherige_kalkulation: "aus der letzten Kalkulation dieser Datei" };
const de = (v) => Number(v).toLocaleString("de-DE");

// Safari-sicher: Komma oder Punkt als Dezimaltrenner ("0,2" / "0.2")
const num = (v) => parseDecimal(v, { maxDecimals: 6 }).value;

export function validateKalk(f, opts, file) {
  const l = opts.limits;
  const errs = [];
  const rng = (v, lo, hi, label, int) => {
    const n = num(v);
    if (String(v ?? "").trim() === "" || n === null) errs.push(`${label} fehlt oder ist ungültig.`);
    else if (int && !Number.isInteger(n)) errs.push(`${label} muss eine ganze Zahl sein.`);
    else if (n < lo || n > hi) errs.push(`${label}: ${de(lo)} – ${de(hi)}.`);
  };
  if (!f.material_id) errs.push("Bitte ein Material wählen.");
  rng(f.quantity, l.quantity_min, l.quantity_max, "Stückzahl", true);
  rng(f.layer_height, l.layer_height_min, l.layer_height_max, "Schichthöhe", false);
  rng(f.wall_lines, l.wall_lines_min, l.wall_lines_max, "Wandlinien", true);
  rng(f.infill, l.infill_min, l.infill_max, "Infill", false);
  if (file && l.max_file_mb && file.size > l.max_file_mb * 1024 * 1024) errs.push(`Datei ist größer als ${de(l.max_file_mb)} MB – wird nicht gesendet.`);
  return errs;
}

// Feld-Optionen aus den Plugin-Limits (gleicher Parser wie überall: "0,2" / "0.2")
const fieldOpts = (l = {}) => ({
  quantity: intOpts(l.quantity_min ?? 1, l.quantity_max ?? 1000),
  layer_height: { min: l.layer_height_min ?? 0, max: l.layer_height_max ?? 10, maxDecimals: 3, decimals: 3, minDecimals: 0 },
  wall_lines: intOpts(l.wall_lines_min ?? 0, l.wall_lines_max ?? 100),
  infill: { min: l.infill_min ?? 0, max: l.infill_max ?? 100, maxDecimals: 1, decimals: 1, minDecimals: 0 },
});

const NumField = ({ id, label, hint, value, onChange, opts }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <NumberField id={id} align="left" opts={opts} value={value} onChange={onChange} testId={id}
      hint={<p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>} />
  </div>
);

export function KalkulationDialog({ orderId, att, onClose, onStarted }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!att) return;
    setData(null); setF(null); setLoadError("");
    api.get(`/auftraege/${orderId}/attachments/${att.id}/kalkulation/vorschlag`)
      .then((r) => { setData(r.data); setF({ ...toFieldText(r.data.params, fieldOpts(r.data.options?.limits)), nozzle_diameter: String(r.data.params.nozzle_diameter) }); })
      .catch((e) => setLoadError(e?.response?.data?.detail || "Optionen der Website konnten nicht geladen werden."));
  }, [orderId, att]);

  const opts = data?.options;
  const errs = opts && f ? validateKalk(f, opts, att) : [];
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/auftraege/${orderId}/attachments/${att.id}/kalkulieren`, { ...f, quantity: num(f.quantity), layer_height: num(f.layer_height), wall_lines: num(f.wall_lines), infill: num(f.infill), nozzle_diameter: Number(f.nozzle_diameter) });
      toast.success(`Kalkulation für ${att.original_name} gestartet.`);
      onStarted();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kalkulation konnte nicht gestartet werden."); }
    finally { setSending(false); }
  };

  const l = opts?.limits || {};
  const fo = fieldOpts(l);
  return (
    <Dialog open={!!att} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="kalk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-blue-600" /> Kalkulieren</DialogTitle>
          <DialogDescription className="break-all" data-testid="kalk-dialog-file">{att?.original_name}</DialogDescription>
        </DialogHeader>
        {loadError && <div className="text-sm p-3 rounded-md border border-rose-200 bg-rose-50 text-rose-800" data-testid="kalk-dialog-load-error">{loadError}</div>}
        {!loadError && !f && <div className="text-sm text-slate-500 flex items-center gap-2 py-6" data-testid="kalk-dialog-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade Optionen der Website…</div>}
        {f && opts && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500" data-testid="kalk-dialog-quelle">Vorbelegt {QUELLE[data.quelle] || ""} · Plugin {opts.plugin_version || "?"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="kalk-material">Material</Label>
                <Select value={f.material_id} onValueChange={set("material_id")}>
                  <SelectTrigger id="kalk-material" data-testid="kalk-material"><SelectValue placeholder="Material wählen" /></SelectTrigger>
                  <SelectContent>{opts.active_materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="kalk-farbe">Farbe</Label>
                <Input id="kalk-farbe" value={f.color} placeholder="z. B. Weiß" onChange={(e) => set("color")(e.target.value)} data-testid="kalk-farbe" />
              </div>
              <NumField id="kalk-stueckzahl" label="Stückzahl" opts={fo.quantity} value={f.quantity} onChange={set("quantity")} hint={`${de(l.quantity_min)} – ${de(l.quantity_max)}`} />
              <NumField id="kalk-schichthoehe" label="Schichthöhe (mm)" opts={fo.layer_height} value={f.layer_height} onChange={set("layer_height")} hint={`${de(l.layer_height_min)} – ${de(l.layer_height_max)} mm`} />
              <NumField id="kalk-wandlinien" label="Wandlinien" opts={fo.wall_lines} value={f.wall_lines} onChange={set("wall_lines")} hint={`${de(l.wall_lines_min)} – ${de(l.wall_lines_max)}`} />
              <NumField id="kalk-infill" label="Infill (%)" opts={fo.infill} value={f.infill} onChange={set("infill")} hint={`${de(l.infill_min)} – ${de(l.infill_max)} %`} />
              <div>
                <Label htmlFor="kalk-duese">Düsendurchmesser (mm)</Label>
                <Select value={f.nozzle_diameter} onValueChange={set("nozzle_diameter")}>
                  <SelectTrigger id="kalk-duese" data-testid="kalk-duese"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.nozzle_diameters.map((n) => <SelectItem key={n} value={String(n)}>{de(n)} mm</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {errs.length > 0 && (
              <div className="text-sm p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 flex gap-2" data-testid="kalk-dialog-errors">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><div>{errs.map((e) => <div key={e}>{e}</div>)}</div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kalk-dialog-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={!f || errs.length > 0 || sending || data?.locked} data-testid="kalk-dialog-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />} Kalkulieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
